"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Mail, ArrowRight, Linkedin, Twitter, Instagram, Loader, CircleCheck, Phone, MapPin, Calendar, Building2, DollarSign } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const SOCIAL_LINKS = [{
  id: 1,
  platform: "linkedin",
  label: "LinkedIn",
  href: "#"
}, {
  id: 2,
  platform: "twitter",
  label: "Twitter",
  href: "#"
}, {
  id: 3,
  platform: "instagram",
  label: "Instagram",
  href: "#"
}];
export default function LetsCollaborate() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="38398" id="request-a-commercial-bid" ref={ref} className={`dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-destructive/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 relative z-10">
        <div className="lg:col-span-7 space-y-8">
          <div className="space-y-4">
            <h2 className="text-4xl md:text-5xl font-sans font-bold tracking-tight">
              Request a Commercial Bid
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Partner with Richmond's trusted electrical experts for your next commercial project. No job is too big or too small.
            </p>
          </div>

          <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-lg p-6 md:p-8 shadow-2xl">
            <FormProvider formId="request_commercial_bid_commercial" sectionName="request-a-commercial-bid" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            project_brief: "",
            budget: "",
            timeline: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            project_brief: {
              required: true
            }
          }} submitText="Request Professional Bid" successMessage="Thank you for your bid request. Our commercial estimating team will review your project details and contact you within 24 business hours.">
              <CollaborateForm />
            </FormProvider>
          </div>
        </div>

        <div className="lg:col-span-5 flex flex-col justify-center lg:justify-start space-y-8 pt-8 lg:pt-24">
          <Card className="bg-card border-border shadow-xl overflow-hidden group hover:border-primary/50 transition-colors duration-300 rounded-lg">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="h-2 w-2 rounded-full bg-destructive animate-pulse" />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-6">
                
                <div className="space-y-1">
                  <h3 className="text-2xl font-bold">Ralf Guzman</h3>
                  <p className="text-muted-foreground text-sm">Master Electrician &amp; Founder&nbsp;<br />Master Electrician License #255427<br />TECL # 255427</p>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-border/50">
                <Link to="mailto:admin@humbergelectric.com" className="flex items-center gap-3 text-foreground hover:text-primary transition-colors group/link">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover/link:bg-primary group-hover/link:text-primary-foreground transition-colors">
                    <Mail className="size-5" />
                  </div>
                  <span className="font-medium">admin@humbergelectric.com</span>
                  <ArrowRight className="size-4 ml-auto opacity-0 group-hover/link:opacity-100 transition-opacity -translate-x-2 group-hover/link:translate-x-0" />
                </Link>

                <div className="flex items-center gap-3 text-muted-foreground">
                  <div className="p-2 rounded-lg bg-muted text-muted-foreground">
                    <Phone className="size-5" />
                  </div>
                  <span>832-405-3766</span>
                </div>

                <div className="flex items-center gap-3 text-muted-foreground">
                  <div className="p-2 rounded-lg bg-muted text-muted-foreground">
                    <MapPin className="size-5" />
                  </div>
                  <span>Richmond, TX & Surrounding Areas</span>
                </div>
              </div>

              <div className="pt-6 flex gap-4">
                {SOCIAL_LINKS.map((social, i) => {
                const Icon = social.platform === "linkedin" ? Linkedin : social.platform === "twitter" ? Twitter : Instagram;
                return <SocialLink key={social.id} icon={<Icon className="size-5" />} href={social.href} label={social.label} data-index={i} />;
              })}
              </div>
            </CardContent>
          </Card>

          <div className="p-6 rounded-lg bg-primary/5 border border-primary/10">
            <h4 className="font-semibold mb-2 text-primary">The Humberg Advantage</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CircleCheck className="size-4 text-primary mt-0.5 shrink-0" />
                <span>Licensed and insured commercial contractors since 2013</span>
              </li>
              <li className="flex items-start gap-2">
                <CircleCheck className="size-4 text-primary mt-0.5 shrink-0" />
                <span>Expertise in complex wiring, panel upgrades, and generator installs</span>
              </li>
              <li className="flex items-start gap-2">
                <CircleCheck className="size-4 text-primary mt-0.5 shrink-0" />
                <span>Reliable 24/7 emergency service and code-compliant workmanship</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>;
}
function SocialLink({
  icon,
  href,
  label,
  "data-index": dataIndex
}: {
  icon: React.ReactNode;
  href: string;
  label: string;
  "data-index"?: number;
}) {
  return <Link to={href} className="p-2 rounded-lg bg-muted hover:bg-primary hover:text-primary-foreground transition-all duration-300 text-muted-foreground" aria-label={label} data-index={dataIndex}>
      {icon}
    </Link>;
}
function CollaborateForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Request Received!</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-lg border-primary text-primary hover:bg-primary/10">
          Request Another Bid
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Your Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="bg-background/50 focus:bg-background transition-colors rounded-lg border-border/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="bg-background/50 focus:bg-background transition-colors rounded-lg border-border/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input type="tel" placeholder="832-405-3766" {...field} className="pl-9 bg-background/50 focus:bg-background transition-colors rounded-lg border-border/50" />
                </div>
              </FormControl>
              <FormDescription>Optional - we may call you to discuss technical details</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization</FormLabel>
              <FormControl>
                <div className="relative">
                  <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Business Name" {...field} className="pl-9 bg-background/50 focus:bg-background transition-colors rounded-lg border-border/50" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_brief" rules={{
      required: "Project Brief is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Project Brief <span className="text-primary">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your commercial project, electrical requirements, and any specific site details..." className="min-h-[120px] bg-background/50 focus:bg-background transition-colors resize-y rounded-lg border-border/50" {...field} />
            </FormControl>
            <FormDescription>Detailed briefs help us provide more accurate initial estimates.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel>Estimated Budget</FormLabel>
              <FormControl>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground z-10" />
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="w-full pl-9 bg-background/50 focus:bg-background transition-colors rounded-lg border-border/50">
                      <SelectValue placeholder="Select a range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Under $5,000">Under $5,000</SelectItem>
                      <SelectItem value="$5,000 - $20,000">$5,000 - $20,000</SelectItem>
                      <SelectItem value="$20,000 - $50,000">$20,000 - $50,000</SelectItem>
                      <SelectItem value="Over $50,000">Over $50,000</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="timeline" render={({
        field
      }) => <FormItem>
              <FormLabel>Desired Timeline</FormLabel>
              <FormControl>
                <div className="relative">
                  <SafeHydrate><Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground z-10" /></SafeHydrate>
                  <Input type="text" {...field} placeholder="e.g. Next 3 months" className="pl-9 bg-background/50 focus:bg-background transition-colors rounded-lg border-border/50" />
                </div>
              </FormControl>
              <FormDescription>Proposed project start date</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg animate-in slide-in-from-top-2">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold py-6 text-base shadow-lg shadow-destructive/20 rounded-lg transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Bid...
          </> : <>
            Request Professional Bid
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}